import React, { useState, useEffect, useCallback } from 'react';
import { productAPI, inventoryAPI } from '../../services/api';
import { extractImages } from '../../utils/imageUtils';
import { useAuth } from '../../context/AuthContext';

// Components
import DashboardSkeleton from './DashboardSkeleton';
import ProductSection from './ProductSection';
import QuoteModal from '../modals/QuoteModal';
import Modal from '../common/Modal';
import ImageModal from '../common/ImageModal';

// Styles
import './DASHBOARD.css';

// DASHBOARD 메인 컴포넌트
const DASHBOARD = ({ onProductClick, onMoreClick, isLoggedIn = false }) => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // 모달 상태
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedImageTitle, setSelectedImageTitle] = useState('');
  const [isLoginAlertOpen, setIsLoginAlertOpen] = useState(false);

  const { globalState } = useAuth(); // 로그인 사용자 정보 가져오기

  // 데이터 로딩
  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);

      try {
        // 날짜 유효성 체크 함수
        const isDateValid = (sDate, eDate) => {
          if (!sDate && !eDate) return true;
          const now = new Date();
          now.setHours(0, 0, 0, 0);
          if (sDate) {
            const start = new Date(sDate);
            start.setHours(0, 0, 0, 0);
            if (now < start) return false;
          }
          if (eDate) {
            const end = new Date(eDate);
            end.setHours(23, 59, 59, 999);
            if (now > end) return false;
          }
          return true;
        };

        // 데이터 처리 함수
        const processData = (data, maxItems = 10, type = 'general') => {
          if (!Array.isArray(data)) return [];
          return data.filter(item => isDateValid(item.sDate, item.eDate)).slice(0, maxItems).map(item => ({
            ...item,
            itemNm: item.itemNm,
            disPrice: item.disPrice,
            salePrice: item.salePrice,
            shipAvDate: item.shipAvDate,
            FILEPATH: item.FILEPATH,
            isSurplus: type === 'surplus',
            isEvent: type === 'event'
          }));
        };

        // API 호출을 독립적으로 처리 (Promise.allSettled 유사 방식 구현)
        // 1. 행사품목 (필수 아님, 에러 시 빈 배열)
        const fetchEventItems = async () => {
          try {
            const data = await productAPI.getDashItems('020');
            return { success: true, data: processData(data, 10, 'event') };
          } catch (error) {
            console.error('행사품목 로드 실패:', error);
            return { success: false, data: [] };
          }
        };

        // 2. 자사재고 (로그인 시에만, 에러 시 빈 배열)
        const fetchInventoryItems = async () => {
          if (!isLoggedIn || !globalState.G_USER_ID) return { success: true, data: [] };

          try {
            const data = await inventoryAPI.getCompanyInventory(globalState.G_USER_ID);
            return { success: true, data: data.slice(0, 12) };
          } catch (error) {
            console.error('자사재고현황 로드 실패:', error);
            // 자사재고는 실패하면 빈 배열로라도 섹션을 보여줄지 결정 필요
            // 기존 로직은 "등록된 자사재고가 없습니다"를 보여주기 위해 빈 배열 반환
            return { success: false, data: [] };
          }
        };

        // 병렬 실행
        const [eventResult, inventoryResult] = await Promise.all([
          fetchEventItems(),
          fetchInventoryItems()
        ]);

        // 데이터 조합
        const formattedData = [];

        // 1. 자사재고 (로그인 시)
        if (isLoggedIn) {
          formattedData.push({
            title: "자사재고현황",
            icon: "📋",
            items: inventoryResult.data
          });
        }

        // 2. 행사품목 (데이터 있으면)
        if (eventResult.data.length > 0) {
          formattedData.push({
            title: "행사 품목",
            icon: "🔥",
            items: eventResult.data
          });
        } else if (!isLoggedIn) {
          // 데이터가 없고 로그인도 안했으면(자사재고도 없으면) 빈 껍데기라도 보여줄지?
          // 기존 로직: 에러 시 행사품목 빈배열 추가
          formattedData.push({ title: "행사 품목", icon: "🔥", items: [] });
        }

        setProducts(formattedData);

      } catch (fatalError) {
        console.error("대시보드 치명적 오류:", fatalError);
        // 치명적 오류 시 기본 구조 유지
        const errorData = [{ title: "행사 품목", icon: "🔥", items: [] }];
        if (isLoggedIn) {
          errorData.unshift({ title: "자사재고현황", icon: "🏢", items: [] });
        }
        setProducts(errorData);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [isLoggedIn, globalState.G_USER_ID]);

  // 상품 클릭 핸들러 (useCallback 적용)
  const handleProductClick = useCallback((product) => {
    if (!isLoggedIn) {
      setIsLoginAlertOpen(true);
      return;
    }
    setSelectedProduct(product);
    setIsModalOpen(true);
    if (onProductClick) {
      onProductClick(product);
    }
  }, [isLoggedIn, onProductClick]);

  // 자사재고 클릭 핸들러 (useCallback 적용)
  const handleInventoryClick = useCallback((inventory) => {
    // 필요 시 구현
  }, []);

  // 이미지 클릭 핸들러 (useCallback 적용)
  const handleImageClick = useCallback((item) => {
    if (item) {
      setSelectedImageTitle(item.itemNm);
      const images = extractImages(item);
      setSelectedImages(images);
      setIsImageModalOpen(true);
    }
  }, []);

  // 더보기 버튼 클릭 핸들러 (useCallback 적용)
  const handleMoreClick = useCallback((listType) => {
    let targetMenuCd = 'HOME';
    if (listType === 'surplus') {
      targetMenuCd = 'SURPLUS';
    } else if (listType === 'event') {
      targetMenuCd = 'EVENT';
    } else if (listType === 'inventory') {
      targetMenuCd = 'CUST0010';
    }

    if (onMoreClick) {
      onMoreClick(targetMenuCd);
    }
  }, [onMoreClick]);

  // 모달 닫기 핸들러
  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  }, []);

  // 로그인 안내 모달 닫기 핸들러
  const handleLoginAlertClose = useCallback(() => {
    setIsLoginAlertOpen(false);
  }, []);

  if (isLoading) {
    return (
      <div className="dash-dashboard-container">
        <DashboardSkeleton isLoggedIn={isLoggedIn} />
      </div>
    );
  }

  return (
    <div className="dash-dashboard-container">
      <div className="dash-dashboard-content">
        {products.map((category) => (
          <ProductSection
            key={category.title}
            category={category}
            onProductClick={handleProductClick}
            onInventoryClick={handleInventoryClick}
            onImageClick={handleImageClick}
            onMoreClick={handleMoreClick}
            isLoggedIn={isLoggedIn}
          />
        ))}
      </div>

      {/* 견적 요청 모달 */}
      <QuoteModal
        product={selectedProduct}
        isOpen={isModalOpen}
        onClose={handleModalClose}
      />

      {/* 로그인 안내 모달 */}
      <Modal
        isOpen={isLoginAlertOpen}
        title="로그인 필요"
        message="상품 정보를 확인하려면 로그인이 필요합니다.&#10;로그인 후 이용해 주세요."
        onConfirm={handleLoginAlertClose}
        confirmText="확인"
      />

      {/* 이미지 확대 모달 */}
      {isImageModalOpen && (
        <ImageModal
          isOpen={isImageModalOpen}
          onClose={() => setIsImageModalOpen(false)}
          images={selectedImages}
          title={selectedImageTitle}
        />
      )}
    </div>
  );
};

export default DASHBOARD;